import React, { useEffect, useRef, useState } from 'react';
import { PageStyled } from './index.styles';
import { useMediaScreen } from 'common/theme/media';
import VoteNumber from 'components/vote-number';
import Icon from '@mui/material/Icon';
import MiniAlert from 'components/mini-alert';
import { ChevronRight, LocalHotel, PublishedWithChanges } from '@mui/icons-material';
import { Box, Button, Link, Rating, Skeleton, Tooltip } from '@mui/material';
import { convertToSlug } from 'common/utils';
import LazyImage from 'components/lazy-image';
import HotelFeatures from 'components/hotel/hotel-features';
import HotelExpeditionInformation from '../hotel-expedition-information';
import { routesPaths } from 'config/routes';
import i18n from 'assets/i18n';
import moment from 'moment';
import CurrencyPrice from 'components/currency-price';
import { t } from 'i18next';

interface HotelTicketCardProps {
    data?: any;
    params?: any;
    isPopular?: boolean;
}

const HotelTicketCard: React.FC<HotelTicketCardProps> = ({ data, params, isPopular = false }) => {
    const { mediaType } = useMediaScreen();
    const isMobil = mediaType === 'tablet' || mediaType === 'mobile';
    const elementRef = useRef<HTMLDivElement | null>(null);
    const [isVisible, setVisible] = useState(false);
    const urlTemp = window.location;
    let queryParams = new URLSearchParams(urlTemp.search);
    let currencyParam = queryParams.get('curr') ? `&curr=${data?.price_currency}` : '';

    let parameter: string = '';
    if (params) {
        parameter = `?checkInDate=${params.start_date}&checkOutDate=${params.end_date}&guest=${params.adults}`;
        if (params.children.length > 0) {
            parameter += `&child=${params.children.join(',')}`;
        }
        parameter += `&shopId=${data.shopping_file_id}`;
        parameter += currencyParam;
    }

    const url = data
        ? `${routesPaths[i18n.language].hotelDetails}/${convertToSlug(data.title)}-F${data.id}${parameter}`
        : '';

    useEffect(() => {
        const observer = new IntersectionObserver(
            (enteries) => {
                enteries.forEach((entery: IntersectionObserverEntry) => {
                    setVisible(entery.isIntersecting);
                });
            },
            {
                rootMargin: '30px 0px 30px 0px',
            }
        );
        elementRef.current && observer.observe(elementRef.current);
        return () => {
            observer.disconnect();
        };
    }, [elementRef.current]);

    const WebTicketCard = () => {
        return data ? (
            <div className="ticket-card web">
                <div className="column-1">
                    <Button href={url} target="_blank" className="thumbnail">
                        {data.pension_label && (
                            <div className="thumbnail-badge">
                                <Icon>check_circle_filled</Icon> {data.pension_label}
                            </div>
                        )}
                        <LazyImage
                            src={`${data.thumbnail}`}
                            alt={`${data.title}`}
                            fallbackSrc="/assets/images/loading.gif"
                        />
                    </Button>
                </div>
                <div className="column-2">
                    <div className="ticket-title">
                        <Link href={url} target="_blank" underline="none" color="inherit">
                            {data.title}
                        </Link>
                        {data.rating > 0 && (
                            <Rating
                                className="ticket-rating"
                                max={Math.floor(data.rating)}
                                name="read-only"
                                size="small"
                                value={Math.floor(data.rating)}
                                readOnly
                            />
                        )}
                    </div>
                    {data.location && (
                        <div className="location">
                            <Box display="flex" alignItems="center" gap="5px">
                                <Icon>map</Icon>
                                <span>{data.location}</span>
                            </Box>
                        </div>
                    )}
                    {data.km > 0 && data.city && (
                        <div className="location-km">
                            <Box display="flex" alignItems="center" gap="5px">
                                <Icon>location_on_filled</Icon>
                                <span>
                                    {t('hotel.widgets.ticketCard.distance', {
                                        city: data.city,
                                        km: data.km,
                                    })}
                                </span>
                            </Box>
                        </div>
                    )}
                    {data.room_label && (
                        <div className="">
                            <HotelFeatures
                                icon={<LocalHotel />}
                                iconColor="primary"
                                background={true}
                                text={data.room_label}
                            />
                        </div>
                    )}
                    <div className="">
                        <HotelExpeditionInformation maxSize={6} data={data.opportunities} />
                    </div>
                    <div className="alert-box">
                        {data.cancellable && (
                            <MiniAlert
                                size="small"
                                showIcon
                                icon={<PublishedWithChanges />}
                                state={'success'}
                                text={t('hotel.widgets.ticketCard.cancellableText')}
                            />
                        )}
                        {data.room_size < 6 && (
                            <MiniAlert
                                size="small"
                                state={'error'}
                                text={t('hotel.widgets.ticketCard.lastRoomSize', {
                                    size: data.room_size,
                                })}
                            />
                        )}
                    </div>
                </div>
                <div className="column-3">
                    <div className="votes">
                        <VoteNumber vote={data.vote} color="secondary" size="small" voteAlign="right" />
                    </div>
                    <div className="bottom-box">
                        <div className="guest-info">
                            <span style={{ whiteSpace: 'nowrap' }}>
                                {t('hotel.widgets.ticketCard.nightDay', {
                                    size: moment(params.end_date).diff(moment(params.start_date), 'days'),
                                })}
                                ,
                            </span>{' '}
                            <span style={{ whiteSpace: 'nowrap' }}>
                                {t('hotel.widgets.ticketCard.adults', {
                                    size: params.adults,
                                })}
                            </span>
                            <span style={{ whiteSpace: 'nowrap' }}>
                                {params.children.length > 0 &&
                                    ', ' +
                                        t('hotel.widgets.ticketCard.children', {
                                            size: params.children.length,
                                        })}
                            </span>
                        </div>
                        {data.discount > 0 && (
                            <div className="price-discount">
                                <Box display="flex" alignItems="center" gap="5px">
                                    <Icon>local_offer</Icon>
                                    <span>
                                        {t('hotel.widgets.ticketCard.priceDiscount', {
                                            discountRate: Number(data.discount).toFixed(0),
                                        })}
                                    </span>
                                </Box>
                            </div>
                        )}
                        <div className="price-box">
                            {data.discount > 0 && (
                                <div className="old-price">
                                    <CurrencyPrice
                                        price={data.discount_price}
                                        symbol={data.price_currency}
                                        fontSize={12}
                                        line={true}
                                        fontWeight={true}
                                        color="#d32f2f"
                                        noCalculate={true}
                                    />
                                </div>
                            )}
                            <div className="ticket-price">
                                <CurrencyPrice
                                    price={data.now_price}
                                    fontSize={20}
                                    symbol={data.price_currency}
                                    fontWeight={true}
                                    noCalculate={true}
                                />
                            </div>

                            <Tooltip
                                arrow
                                title={t('hotel.widgets.ticketCard.priceInfo')}
                                placement="top"
                                componentsProps={{
                                    tooltip: {
                                        className: 'primary',
                                    },
                                }}
                            >
                                <Icon className="price-info" color="disabled">
                                    info_filled
                                </Icon>
                            </Tooltip>
                        </div>
                        <div className="buttons">
                            <Button
                                href={url}
                                target="_blank"
                                variant="contained"
                                color="primary"
                                endIcon={<ChevronRight />}
                                onClick={(e: any) => {
                                    e.stopPropagation();
                                }}
                            >
                                {t('hotel.widgets.ticketCard.reviewButton')}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        ) : (
            <WebSkeleton />
        );
    };
    const MobilTicketCard = () => {
        return data ? (
            <div className="ticket-card mobil">
                <div className="column-1">
                    <Button href={url} target="_blank" className="thumbnail">
                        <LazyImage
                            src={`${data.thumbnail}`}
                            alt={`${data.title}`}
                            fallbackSrc="/assets/images/loading.gif"
                        />
                    </Button>
                </div>
                <div className="column-2">
                    <div className="ticket-title">
                        <Link href={url} target="_blank" underline="none" color="inherit" className="title">
                            {data.title}
                        </Link>
                        {data.rating > 0 && (
                            <Rating
                                className="ticket-rating"
                                max={Math.floor(data.rating)}
                                name="read-only"
                                size="small"
                                value={Math.floor(data.rating)}
                                readOnly
                            />
                        )}
                        {data.room_size < 6 && (
                            <MiniAlert
                                size="small"
                                state={'error'}
                                text={t('hotel.widgets.ticketCard.lastRoomSize', {
                                    size: data.room_size,
                                })}
                            />
                        )}
                    </div>
                    <div className="votes">
                        <VoteNumber vote={data.vote} color="secondary" size="small" voteAlign="right" />
                    </div>
                </div>
                <div className="column-3">
                    {data.location && (
                        <div className="location">
                            <Box display="flex" alignItems="center" gap="5px">
                                <Icon>map</Icon>
                                <span>{data.location}</span>
                            </Box>
                        </div>
                    )}
                    {data.km > 0 && data.city && (
                        <div className="location-km">
                            <Box display="flex" alignItems="center" gap="5px">
                                <Icon>location_on_filled</Icon>
                                <span>
                                    {t('hotel.widgets.ticketCard.distance', {
                                        city: data.city,
                                        km: data.km,
                                    })}
                                </span>
                            </Box>
                        </div>
                    )}
                </div>
                <div className="column-4">
                    {data.room_label && (
                        <HotelFeatures
                            icon={<LocalHotel />}
                            iconColor="primary"
                            background={true}
                            text={data.room_label}
                        />
                    )}
                    {data.cancellable && (
                        <MiniAlert
                            size="small"
                            showIcon
                            icon={<PublishedWithChanges />}
                            state={'success'}
                            text={t('hotel.widgets.ticketCard.cancellableText')}
                        />
                    )}
                </div>
                <div className="column-5">
                    <div>
                        <div className="guest-info">
                            <span style={{ whiteSpace: 'nowrap' }}>
                                {t('hotel.widgets.ticketCard.nightDay', {
                                    size: moment(params.end_date).diff(moment(params.start_date), 'days'),
                                })}
                            </span>
                            {', '}
                            <span style={{ whiteSpace: 'nowrap' }}>
                                {t('hotel.widgets.ticketCard.adults', {
                                    size: params.adults,
                                })}
                            </span>
                            <span style={{ whiteSpace: 'nowrap' }}>
                                {params.children.length > 0 &&
                                    ', ' +
                                        t('hotel.widgets.ticketCard.children', {
                                            size: params.children.length,
                                        })}
                            </span>
                        </div>

                        <div className="price-box">
                            {data.discount > 0 && (
                                <div className="old-price">
                                    <CurrencyPrice
                                        price={data.discount_price}
                                        symbol={data.price_currency}
                                        fontSize={12}
                                        line={true}
                                        fontWeight={true}
                                        color="#d32f2f"
                                        noCalculate={true}
                                    />
                                </div>
                            )}
                            <Box display="flex" flexDirection="row" alignItems="center" gap="5px">
                                <div className="ticket-price">
                                    <CurrencyPrice
                                        price={data.now_price}
                                        fontSize={20}
                                        symbol={data.price_currency}
                                        fontWeight={true}
                                        noCalculate={true}
                                    />
                                </div>

                                <Tooltip
                                    arrow
                                    title={t('hotel.widgets.ticketCard.priceInfo')}
                                    placement="top"
                                    componentsProps={{
                                        tooltip: {
                                            className: 'primary',
                                        },
                                    }}
                                >
                                    <Icon className="price-info" color="disabled">
                                        info_filled
                                    </Icon>
                                </Tooltip>
                            </Box>
                        </div>
                    </div>
                    <div>
                        {data.discount > 0 && (
                            <div className="price-discount">
                                <Box display="flex" alignItems="center" gap="5px">
                                    <Icon>local_offer</Icon>
                                    <span>
                                        {t('hotel.widgets.ticketCard.priceDiscount', {
                                            discountRate: Number(data.discount).toFixed(0),
                                        })}
                                    </span>
                                </Box>
                            </div>
                        )}
                        <div className="buttons">
                            <Button
                                href={url}
                                target="_blank"
                                variant="contained"
                                color="primary"
                                endIcon={<ChevronRight />}
                                onClick={(e: any) => {
                                    e.stopPropagation();
                                }}
                            >
                                {t('hotel.widgets.ticketCard.reviewButton')}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        ) : (
            <MobilSkeleton />
        );
    };
    const WebSkeleton = () => {
        return (
            <div className="ticket-card web">
                <div className="column-1">
                    <Skeleton animation="wave" variant="rectangular" width={230} height={230} />
                </div>
                <div className="column-2">
                    <Skeleton animation="wave" width={'70%'} height={25} />
                    <div className="location">
                        <Box display="flex" alignItems="center" gap="5px">
                            <Skeleton animation="wave" width={'80%'} height={25} />
                        </Box>
                    </div>
                    <div className="location-km">
                        <Box display="flex" alignItems="center" gap="5px">
                            <Skeleton animation="wave" width={'50%'} height={25} />
                        </Box>
                    </div>
                    <div className="">
                        <Skeleton animation="wave" width={'25%'} height={25} />
                    </div>
                    <Box display="flex" alignItems="center" gap="5px">
                        <Skeleton animation="wave" variant="circular" width={25} height={25} />
                        <Skeleton animation="wave" variant="circular" width={25} height={25} />
                        <Skeleton animation="wave" variant="circular" width={25} height={25} />
                        <Skeleton animation="wave" variant="circular" width={25} height={25} />
                    </Box>
                    <div className="alert-box">
                        <Skeleton animation="wave" width={50} height={25} />
                        <Skeleton animation="wave" width={50} height={25} />
                    </div>
                </div>
                <div className="column-3">
                    <div className="votes">
                        <Skeleton animation="wave" width={120} height={28} />
                    </div>
                    <div className="bottom-box">
                        <div className="guest-info">
                            <Skeleton animation="wave" width={120} height={36} />
                        </div>

                        <div className="price-box">
                            <div className="ticket-price">
                                <Skeleton animation="wave" width={90} height={28} />
                            </div>
                        </div>
                        <div className="buttons">
                            <Skeleton animation="wave" variant="rounded" width={130} height={36} />
                        </div>
                    </div>
                </div>
            </div>
        );
    };
    const MobilSkeleton = () => {
        return (
            <div className="ticket-card mobil">
                <div className="column-1">
                    <Skeleton animation="wave" variant="rectangular" width={'100%'} height={230} />
                </div>

                <div className="column-2">
                    <Skeleton animation="wave" width={'70%'} height={45} />
                    <Skeleton animation="wave" width={45} height={45} />
                </div>
                <div className="column-3">
                    <Skeleton animation="wave" width={'70%'} height={25} />
                    <Skeleton animation="wave" width={'50%'} height={25} />
                </div>
                <div className="column-4">
                    <Skeleton animation="wave" width={'30%'} height={25} />
                    <Skeleton animation="wave" width={'30%'} height={25} />
                </div>
                <div className="column-5">
                    <Skeleton animation="wave" width={'30%'} height={35} />
                    <Skeleton animation="wave" width={'30%'} height={35} />
                </div>
            </div>
        );
    };

    return (
        <PageStyled>
            <div
                className={`ticket ${
                    data?.price_index > 0 ? `cheapest cheapest-${data.price_index}` : isPopular ? 'popular' : ''
                } ${isMobil ? 'mobil' : 'web'} ${isVisible ? 'fade-in' : ''} ${data ? '' : 'no-animate'}`}
                ref={elementRef}
            >
                {isMobil ? <MobilTicketCard /> : <WebTicketCard />}

                {data?.price_index > 0 && (
                    <MiniAlert
                        text={
                            <Box display="flex" flexDirection="row" justifyContent="center" alignItems="center" gap={1}>
                                <img src="/assets/images/enucuzu-fligram.svg" alt="En Ucuzu" height={10} />
                                <div className="badge">{data.price_index}</div>
                            </Box>
                        }
                        className="alert-cheapest-card"
                    />
                )}
            </div>
        </PageStyled>
    );
};

export default HotelTicketCard;
